package org.example.project.login

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.example.project.settings.UISettings
import org.example.project.settings.LumaticaCheckbox
import org.example.project.settings.LogoSettings
import org.example.project.registrierung.NetzwerkTeilnehmerUebersicht
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

object UnifiedAccessStyles : StyleSheet() {
    val layout by style {
        width(100.percent); height(100.vh)
        display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
        boxSizing("border-box"); padding(24.px); position(Position.Relative)
    }
    val stage by style {
        position(Position.Relative)
        backgroundColor(Color.transparent)
    }

    val panel by style {
        width(100.percent)
        maxWidth(410.px)
        padding(28.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        justifyContent(JustifyContent.Center)
        gap(16.px)
        boxSizing("border-box")
        backgroundColor(rgba(8, 10, 14, 0.56))
        property("backdrop-filter", "blur(18px)")
        property("border", "1px solid rgba(255,255,255,0.14)")
        borderRadius(18.px)
        property("box-shadow", "0 24px 70px rgba(0,0,0,0.38), inset 0 1px 0 rgba(255,255,255,0.04)")
        property("transform", "none")
    }

    val desktopFlow by style {
        width(100.percent); display(DisplayStyle.Flex)
        alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); gap(72.px)
    }

    val mapPane by style {
        width(420.px); height(550.px)
        display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
        flexShrink(0)
    }

    val divider by style {
        width(3.px); height(550.px); flexShrink(0)
        margin(0.px, 48.px)
        borderRadius(999.px)
        property("background", "linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.92), rgba(255,255,255,0.05))")
        property("box-shadow", "0 0 24px rgba(255,255,255,0.38)")
    }

    val logo by style {
        width(180.px)
        height(52.px)
        property("object-fit", "contain")
        property("align-self", "center")
        marginBottom(8.px)
    }

    val input by style {
        width(100.percent); height(50.px); padding(0.px, 14.px)
        boxSizing("border-box"); borderRadius(8.px)
        backgroundColor(Color.transparent); color(Color.white)
        property("border", "1px solid rgba(255,255,255,0.18)")
        property("box-shadow", "0 0 0 1px rgba(255,255,255,0.05), 0 0 18px rgba(255,255,255,0.08)")
        fontSize(15.px); textAlign("center")
        focus { outline("none"); property("border-color", "rgba(255,255,255,0.62)") }
        media(mediaMaxWidth(768.px)) { self style { height(44.px) } }
    }

    val primaryButton by style {
        width(100.percent); height(50.px); padding(0.px, 16.px)
        boxSizing("border-box"); borderRadius(8.px)
        display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
        backgroundColor(Color(UISettings.LOGIN_BUTTON_BG)); color(Color.white)
        property("border", "1px solid ${UISettings.accentRgba(0.72)}")
        property("box-shadow", "0 12px 30px ${UISettings.accentRgba(0.32)}, inset 0 1px 0 rgba(255,255,255,0.18)")
        fontSize(15.px); fontWeight("600"); cursor("pointer")
        hover {
            backgroundColor(UISettings.ACCENT)
            property("box-shadow", "0 15px 34px ${UISettings.accentRgba(0.42)}, inset 0 1px 0 rgba(255,255,255,0.22)")
        }
        disabled { opacity(0.4); cursor("not-allowed") }
        media(mediaMaxWidth(768.px)) { self style { height(44.px) } }
    }

    val secondaryButton by style {
        width(100.percent); height(50.px); padding(0.px, 16.px)
        boxSizing("border-box"); borderRadius(8.px)
        display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
        backgroundColor(Color.transparent); color(Color.white)
        property("border", "1px solid rgba(255,255,255,0.18)")
        fontSize(15.px); fontWeight("600"); cursor("pointer")
        disabled { opacity(0.4); cursor("not-allowed") }
        media(mediaMaxWidth(768.px)) { self style { height(44.px) } }
    }

    val switchRow by style {
        display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
        gap(6.px); marginTop(10.px); fontSize(13.px); color(Color("#9b9fa7"))
    }

    val optionRow by style {
        width(100.percent); display(DisplayStyle.Flex); alignItems(AlignItems.Center)
        justifyContent(JustifyContent.SpaceBetween); gap(12.px); fontSize(13.px); color(Color("#b8bbc2"))
    }

    val rememberGroup by style {
        display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px)
    }

    val switchAction by style {
        color(Color("#f2f2f3")); fontWeight("600"); cursor("pointer")
        property("text-decoration", "none")
        hover { color(Color("#ff3049")) }
    }

    val textLink by style {
        property("align-self", "center"); color(Color("#b8bbc2")); fontSize(13.px)
        cursor("pointer"); property("text-decoration", "none")
        hover { color(Color.white) }
    }

    val infoText by style {
        width(100.percent)
        color(Color("#d6d8dd"))
        fontSize(13.px)
        lineHeight("1.4")
        textAlign("center")
    }

    val mobilePanel by style {
        media(mediaMaxWidth(768.px)) {
            self style {
                width(90.percent)
                padding(24.px)
            }
        }
    }

}

@Composable
fun UnifiedLoginLayout(
    isMobile: Boolean,
    accessory: (@Composable () -> Unit)? = null,
    content: @Composable () -> Unit
) {
    Style(UnifiedAccessStyles)
    Style(LoginStyles)
    Div({ classes(UnifiedAccessStyles.layout, "public-access-stage") }) {
        if (!isMobile) {
            Div({ classes(UnifiedAccessStyles.mapPane) }) {
                NetzwerkTeilnehmerUebersicht(compact = true)
            }
            Div({ classes(UnifiedAccessStyles.divider) })
        }
        Div({ classes(UnifiedAccessStyles.panel, UnifiedAccessStyles.mobilePanel) }) {
            Img(src = LogoSettings.desktopRegistrationHeaderLogo, attrs = { classes(UnifiedAccessStyles.logo) })
            accessory?.invoke()
            content()
        }
    }
}

@Composable
fun UnifiedAccessButton(label: String, enabled: Boolean, secondary: Boolean = false, onClick: () -> Unit) {
    Button(attrs = {
        classes(if (secondary) UnifiedAccessStyles.secondaryButton else UnifiedAccessStyles.primaryButton)
        if (!enabled) disabled()
        onClick { if (enabled) onClick() }
    }) { Text(label) }
}

@Composable
fun UnifiedLoginContent(
    identifier: String,
    password: String,
    identifierPlaceholder: String,
    loading: Boolean,
    submitLabel: String = "Anmelden",
    rememberLabel: String = "Angemeldet bleiben",
    errorText: String? = null,
    infoText: String? = null,
    forgotLabel: String? = null,
    switchPrefix: String? = null,
    switchLabel: String? = null,
    onIdentifierChange: (String) -> Unit,
    onPasswordChange: (String) -> Unit,
    onSubmit: () -> Unit,
    onForgot: (() -> Unit)? = null,
    onSwitch: (() -> Unit)? = null
) {
    var rememberMe by remember { mutableStateOf(false) }
    Input(type = InputType.Email, attrs = {
        classes(UnifiedAccessStyles.input)
        value(identifier); placeholder(identifierPlaceholder)
        onInput { onIdentifierChange(it.value) }
        onKeyDown { if (it.key == "Enter") { it.preventDefault(); onSubmit() } }
    })
    Input(type = InputType.Password, attrs = {
        classes(UnifiedAccessStyles.input)
        value(password); placeholder("Passwort")
        onInput { onPasswordChange(it.value) }
        onKeyDown { if (it.key == "Enter") { it.preventDefault(); onSubmit() } }
    })
    if (!errorText.isNullOrBlank()) Div({ classes(LoginStyles.error) }) { Text(errorText) }
    if (!infoText.isNullOrBlank()) Div({ classes(UnifiedAccessStyles.infoText) }) { Text(infoText) }
    Div({ classes(UnifiedAccessStyles.optionRow) }) {
        Div({ classes(UnifiedAccessStyles.rememberGroup) }) {
            LumaticaCheckbox(checked = rememberMe, onChange = { rememberMe = it }, enabled = !loading, fontSizePx = 14)
            Span { Text(rememberLabel) }
        }
        if (forgotLabel != null && onForgot != null) {
            Span({ classes(UnifiedAccessStyles.textLink); onClick { if (!loading) onForgot() } }) { Text(forgotLabel) }
        }
    }
    UnifiedAccessButton(if (loading) "..." else submitLabel, !loading, onClick = onSubmit)
    if (switchPrefix != null && switchLabel != null && onSwitch != null) {
        Div({ classes(UnifiedAccessStyles.switchRow) }) {
            Span { Text(switchPrefix) }
            Span({ classes(UnifiedAccessStyles.switchAction); onClick { onSwitch() } }) { Text(switchLabel) }
        }
    }
}
